'use client';

import type { AnimationPlaybackControls, Easing } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { animate, useMotionValue, useMotionValueEvent } from 'motion/react';

import type { Direction } from './grading';
import { Glyphs } from './glyphs';
import { clampTravel, easingFor, milestoneJustPassed, rollStart } from './grading';
import { DUR_NUMBER } from './tokens';
import { useMotionReduced } from './use-motion-reduced';

type Quantiser = { readonly from: number; readonly to: number; readonly steps: number };

type AnimatedNumberProps = {
  readonly value: number;
  readonly format?: (value: number) => string;
  readonly direction?: Direction;
  readonly live?: boolean;
  readonly duration?: number;
  readonly delay?: number;
  /** Open at this fraction of the value instead of one unit below it. */
  readonly startAt?: number;
  /** Count from here (travel capped, see `clampTravel`); the length then follows the distance. */
  readonly from?: number;
  /** Land on this many figures instead of rolling through every one; 0 is a continuous roll. */
  readonly steps?: number;
  readonly ease?: Easing;
  /** Render the figure's own `sr-only` copy. `false` when a parent supplies the accessible text for a composite. */
  readonly labelled?: boolean;
  readonly className?: string;
};

function defaultFormat(value: number): string {
  return String(Math.round(value));
}

function quantise(value: number, q: Quantiser | null): number {
  if (q === null || q.steps === 0) return value;
  const span = q.to - q.from;
  if (span === 0) return q.to;
  const t = (value - q.from) / span;
  return q.from + span * (Math.round(t * q.steps) / q.steps);
}

/**
 * The last-digits principle: the leading digits hold still and the tail rolls, so the value is true the whole
 * time and the motion reads as the figure landing. The first paint is the final number; the roll is applied
 * after mount, on this figure's beat.
 */
function AnimatedNumber({
  value,
  format = defaultFormat,
  direction,
  live = true,
  duration = DUR_NUMBER,
  delay = 0,
  startAt,
  from: fromProp,
  steps = 0,
  ease,
  labelled = true,
  className,
}: AnimatedNumberProps) {
  const safe = Number.isFinite(value) ? value : 0;
  const reduced = useMotionReduced();
  const motionValue = useMotionValue(safe);
  const stepRef = useRef<Quantiser | null>(null);
  const [shown, setShown] = useState(safe);
  useMotionValueEvent(motionValue, 'change', (current) => setShown(quantise(current, stepRef.current)));

  // Keyed on the curve's values, not the array's identity: a caller passing `bezier(...)` or a literal hands
  // over a fresh array every render, and as a raw dep that restarts the roll each frame and pins it at `from`.
  const easeRef = useRef(ease);
  easeRef.current = ease;
  const easeKey = Array.isArray(ease) ? ease.join(',') : String(ease);

  useEffect(() => {
    if (!live || reduced) {
      stepRef.current = null;
      motionValue.set(safe);
      setShown(safe);
      return;
    }
    const dir = direction ?? (safe >= 0 ? 'up' : 'down');
    const hasFrom = fromProp != null && Number.isFinite(fromProp);
    const from = hasFrom ? clampTravel(safe, fromProp) : startAt != null ? safe * startAt : rollStart(safe);
    const span = Math.abs(safe - from) / Math.max(Math.abs(safe), 1e-9);
    const travel = hasFrom ? Math.min(2.2, 0.42 + span * 9) : duration;
    const dur = (startAt == null && milestoneJustPassed(safe) != null ? 3.2 : 1) * travel;
    let controls: AnimationPlaybackControls | undefined;
    // Hold the true figure until this number's beat, then drop back and roll: showing `from` through the delay
    // read as a wrong value that froze and twitched late.
    const start = () => {
      stepRef.current = { from, to: safe, steps };
      motionValue.set(from);
      controls = animate(motionValue, safe, { duration: dur, ease: easeRef.current ?? easingFor(dir) });
    };
    const timer = delay > 0 ? setTimeout(start, delay * 1000) : null;
    if (timer === null) start();
    return () => {
      if (timer !== null) clearTimeout(timer);
      controls?.stop();
    };
  }, [safe, live, reduced, duration, delay, direction, startAt, fromProp, steps, easeKey, motionValue]);

  return (
    <span data-slot="animated-number" className={cn('inline-flex tabular-nums', className)}>
      {labelled && <span className="sr-only">{format(safe)}</span>}
      <Glyphs text={format(shown)} />
    </span>
  );
}

export { AnimatedNumber };
export type { AnimatedNumberProps };
